import React, { useState } from 'react'
import { useGameContext } from '../GameContext'
import { useAuthFromContext } from '../AuthContext'
import MatchHistory from './MatchHistory'
import TutorialV2Board from './TutorialV2Board'
import RulesModal from './RulesModal'
import HogTitleGroup from './HogTitleGroup'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCopy } from '@fortawesome/free-regular-svg-icons'
import { faCheck, faHourglassHalf } from '@fortawesome/free-solid-svg-icons'
import './LobbyScreen.css'
import { isTestMode, isTestModeByDefault, testModeToggleUrl } from '../utils/testMode'

const VISITED_KEY = 'hogofwar_visited'

export default function LobbyScreen() {
    const { lobbyStatus, myRoomCode, roomError, setRoomError, isJoining, game, createRoom, createBotWar, joinRoom, returnToLobby, renewRoom } = useGameContext()
    const { user, userRecord, signOut } = useAuthFromContext()
    const [codeInput, setCodeInput] = useState('')
    const [showHistory, setShowHistory] = useState(false)
    const [showTutorial, setShowTutorial] = useState(false)
    const [rulesOpen, setRulesOpen] = useState(false)
    const [hasVisited, setHasVisited] = useState(() => !!localStorage.getItem(VISITED_KEY))
    const [copied, setCopied] = useState(false)

    // Test mode is URL-driven (utils/testMode): the toggle just navigates to
    // the URL that flips it, and the tag reminds the tester it's on so a real
    // war never lands in the test archive by accident.
    const testMode = isTestMode()
    function toggleTestMode() {
        window.location.assign(testModeToggleUrl({
            href: window.location.href, on: !testMode, defaultOn: isTestModeByDefault(),
        }))
    }
    const testTag = testMode ? <p className="lobby-test-tag">Test war</p> : null

    function markVisited() {
        if (!hasVisited) {
            localStorage.setItem(VISITED_KEY, '1')
            setHasVisited(true)
        }
    }

    function handleTutorialOpen() {
        markVisited()
        setShowTutorial(true)
    }

    function handleTutorialComplete() {
        setShowTutorial(false)
    }

    function handleCreateRoom() {
        markVisited()
        createRoom()
    }

    function handleJoin(e) {
        e.preventDefault()
        if (!codeInput.trim()) return
        markVisited()
        joinRoom(codeInput)
    }

    function handleCodeInput(e) {
        setRoomError(null)
        setCodeInput(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ''))
    }

    function handleCopy() {
        navigator.clipboard.writeText(myRoomCode).then(() => {
            setCopied(true)
            setTimeout(() => setCopied(false), 750)
        })
    }

    if (showHistory) {
        return <MatchHistory onBack={() => setShowHistory(false)} />
    }

    if (showTutorial) {
        return <TutorialV2Board onComplete={handleTutorialComplete} />
    }

    if (lobbyStatus === 'waiting') {
        const isP1 = game?.player1 === user?.uid
        const oppJoined = !!game?.player2
        const oppHomefrontReady = game
            ? (isP1 ? game.p2HomefrontReady : game.p1HomefrontReady)
            : false
        const oppSettingUp = oppJoined && !oppHomefrontReady

        if (oppSettingUp) {
            return (
                <div className="lobby-screen">
                    <HogTitleGroup titleClassName="lobby-title" />
                    {testTag}
                    <div className="lobby-waiting-body">
                        <div className="lobby-opp-setup-header">
                            <FontAwesomeIcon icon={faHourglassHalf} className="lobby-opp-setup-icon" />
                            <p className="lobby-opp-setup-msg">Your opponent is setting up their grid...</p>
                        </div>
                        <button className={`lobby-opp-setup-code${copied ? ' lobby-opp-setup-code--copied' : ''}`} onClick={handleCopy} disabled={!myRoomCode}>
                            Room: {myRoomCode ?? '...'}
                            <FontAwesomeIcon icon={copied ? faCheck : faCopy} className="lobby-opp-setup-code__icon" />
                        </button>
                        <button className="lobby-btn-ghost" onClick={returnToLobby}>
                            Cancel
                        </button>
                    </div>
                    <div className="lobby-footer">
                        <button className="lobby-footer-link" onClick={() => setShowHistory(true)}>
                            Match History
                        </button>
                    </div>
                </div>
            )
        }

        const waitingMessage = !oppJoined
            ? 'Waiting for opponent to join...'
            : 'Starting war...'

        return (
            <div className="lobby-screen">
                <HogTitleGroup titleClassName="lobby-title" />
                {testTag}
                <p className="lobby-label">Share this code with your opponent</p>
                <div className="lobby-waiting-body">
                    {myRoomCode ? (
                        <div className="lobby-code-row" onClick={handleCopy} title="Copy code">
                            <div className="lobby-room-code"><span className="lobby-room-code__text">{myRoomCode}</span></div>
                            <div className={`lobby-copy-btn${copied ? ' lobby-copy-btn--copied' : ''}`}>
                                <FontAwesomeIcon icon={copied ? faCheck : faCopy} />
                            </div>
                        </div>
                    ) : (
                        <div className="lobby-room-code lobby-room-code--loading"><span className="lobby-room-code__text">...</span></div>
                    )}
                    <button className="lobby-new-code-btn" onClick={renewRoom} disabled={!myRoomCode}>[ Get new code ]</button>
                    <p className="lobby-waiting-text">{waitingMessage}</p>
                    <button className="lobby-btn-ghost" onClick={returnToLobby}>
                        Cancel
                    </button>
                </div>
                <div className="lobby-footer">
                    <button className="lobby-footer-link" onClick={() => setShowHistory(true)}>
                        Match History
                    </button>
                </div>
            </div>
        )
    }

    return (
        <div className="lobby-screen">
            <RulesModal isVisible={rulesOpen} onClose={() => setRulesOpen(false)} />

            <HogTitleGroup titleClassName="lobby-title" />
            {testTag}

            {userRecord && (
                <p className="lobby-user-greeting">Signed in as <strong>{userRecord.displayName}</strong></p>
            )}

            {!hasVisited && (
                <p className="lobby-new-nudge">New? Start here &darr;</p>
            )}

            <div className="lobby-learn-row">
                <button
                    className={`lobby-learn-btn lobby-learn-btn--tutorial${!hasVisited ? ' lobby-learn-btn--highlight' : ''}`}
                    onClick={handleTutorialOpen}
                >
                    Tutorial
                </button>
                <button
                    className={`lobby-learn-btn lobby-learn-btn--rules${!hasVisited ? ' lobby-learn-btn--highlight' : ''}`}
                    onClick={() => setRulesOpen(true)}
                >
                    Rules
                </button>
            </div>

            <div className="lobby-actions">
                <div className="lobby-card">
                    <p className="lobby-label">Start a new match</p>
                    <button className="lobby-btn-primary" onClick={handleCreateRoom}>
                        Start a War
                    </button>
                    <button className="lobby-btn-ghost" onClick={() => createBotWar?.()}>
                        Play the Bot
                    </button>
                </div>

                <div className="lobby-divider"><span>or</span></div>

                <div className="lobby-card">
                    <p className="lobby-label">Enter a war code</p>
                    <form onSubmit={handleJoin}>
                        <input
                            className="lobby-code-input"
                            type="text"
                            value={codeInput}
                            onChange={handleCodeInput}
                            placeholder="A7K2P"
                            maxLength={5}
                            autoComplete="off"
                            autoCapitalize="characters"
                            spellCheck={false}
                        />
                        {roomError && <p className="lobby-error">{roomError}</p>}
                        <button
                            className="lobby-btn-primary"
                            type="submit"
                            disabled={!codeInput.trim() || isJoining}
                        >
                            {isJoining ? 'Joining...' : 'Join Match'}
                        </button>
                    </form>
                </div>
            </div>

            <div className="lobby-footer">
                <button className="lobby-footer-link" onClick={() => setShowHistory(true)}>
                    Match History
                </button>
                <button className="lobby-footer-link" onClick={signOut}>
                    Sign Out
                </button>
                <button
                    className="lobby-footer-link lobby-footer-link--faint"
                    onClick={toggleTestMode}
                    title="Test wars are archived apart from real ones"
                >
                    {testMode ? 'exit test mode' : 'test mode'}
                </button>
            </div>
        </div>
    )
}
